import { Injectable } from '@angular/core';

import {
  ItemStatus,
  OrderItem,
  OrderItemCreate,
  OrderOutput,
  OrderStatus,
  PropertyData,
  SendCancellationEMailCommand,
  SendPOAEMailCommand,
  UserData,
} from 'frontend.client.api';
import { ApiService } from '../../shared/api.service';
import {
  PartnerState,
  PartnerStateService,
} from '../../shared/partner-state.service';
import { RouterService, RouterState } from '../../shared/router.service';
import { ThresholdService } from '../../shared/threshold.service';
import { TokenService } from '../../shared/token.service';
import { AccessHashService } from '../access-hash.service';
import { AccessIdService, AccessIdState } from '../access-id.service';
import { ProductService } from '../product.service';
import { OrderServiceAPI } from './order.service.api';

export interface ChangeableOrder {
  items?: OrderItemCreate[];
  userData?: UserData;
  propertyData?: PropertyData;
}
@Injectable({
  providedIn: 'root',
})
export class OrderService extends OrderServiceAPI {
  constructor(
    protected apiService: ApiService,
    protected partnerStateService: PartnerStateService,
    protected router: RouterService,
    protected accessHashService: AccessHashService,
    protected thresholdService: ThresholdService,
    protected productService: ProductService,
    protected tokenService: TokenService,
    protected accessIdService: AccessIdService
  ) {
    super(
      apiService,
      partnerStateService,
      router,
      accessHashService,
      thresholdService,
      tokenService,
      accessIdService
    );
    this.accessIdService.accessIdState$.subscribe((state) => {
      this.changedUpstreamStates(state, this.partnerStateService.partnerState.getValue());
    });

    // this.accessHashService.hashState.subscribe((hash) => {
    //   this.accessHash = hash;
    //   if (this.orderState$.getValue() === undefined && this.accessIdService.currentAccessId()) {
    //     this.getOrderByAccessID();
    //   }
    // });

    this.partnerStateService.partnerState.subscribe((partner) => {
      this.changedUpstreamStates(this.accessIdService.accessIdState$.getValue(), partner);
    });

    this.router.getState().subscribe((router) => {
      console.log(router);
      this.changedUpstreamStates(this.accessIdService.accessIdState$.getValue(), this.partnerStateService.partnerState.getValue());
      // this.handleAccessIdUpdate(router.query?.accessId);
      // if (router.initialized && this.routerAccessIdIsUndefined(router)) {
      //   this.ensureOrder();
      // }
    });

    this.orderState$.subscribe((orderState) => {
      const order = orderState.order;
      if (
        order?.accessId &&
        order?.accessId !== accessIdService.currentAccessId() &&
        accessIdService.currentAccessId() === undefined
      ) {
        this.accessIdService.setAccessId(order.accessId);
      }

      if (order) {
        this.accessHashService.setBirthday(
          order?.userData?.userPersonalData?.birthday,
        );
      }

      // handle routing based on order

      if (!order) {
        this.ensureOrder();
      }
    });
  }

  private changedUpstreamStates(accessState: AccessIdState, partner: PartnerState) {
    if (partner.partnerData && accessState.initialized) {
      this.ensureOrder()
    }

  }

  private checkRouting(router?: RouterState) {
    const state = router ?? this.router.getValue();
    // update accessId if routerState subscription triggered update
    if (router && state.initialized && state.query?.accessId) {
      this.handleAccessIdUpdate(state.query?.accessId);
    }
    if (state.initialized && this.routerAccessIdIsUndefined(state)) {
      this.ensureOrder();
    }
    this.handleRouting(state, this.orderState$.getValue().order);
  }

  private handleRouting(routerState: RouterState, order?: OrderOutput) {
    const pagesToRouteFrom: string[] = [
      'unterlagenauswahl',
      'dateneingabe',
      'zahlung',
    ];
    if (
      order &&
      routerState.initialized &&
      this.partnerStateService.partner &&
      pagesToRouteFrom.includes(routerState.path ?? '')
    ) {
      switch (order.orderStatus) {
        case OrderStatus.CREATED:
          break;
        case OrderStatus.DELIVERED:
        case OrderStatus.COMMISSIONED:
          this.router.routeToInCurrentSubfolder('lieferstatus');
          break;
        case OrderStatus.ERROR:
          break; // TODO: handle error
        case OrderStatus.IN_PAYMENT:
          this.router.routeToInCurrentSubfolder('zahlung');
          break;
        case OrderStatus.PAYMENT_SUCCESS:
          this.router.routeToInCurrentSubfolder('bestaetigung');
          break;
      }
    } else if (!order && routerState.initialized && this.partnerStateService.partner) {
      this.ensureOrder();
    } else if (
      order?.items.length === 0 &&
      routerState.initialized &&
      this.partnerStateService.partner
    ) {
      this.router.routeToInCurrentSubfolder('unterlagenauswahl');
    }
  }

  private routerAccessIdIsUndefined(routerState: RouterState) {
    return (
      routerState.query?.accessId === undefined ||
      routerState.query?.accessId === ''
    );
  }

  private routerInitializedAndNoAccessId(routerState: RouterState) {
    const result =
      routerState.initialized && this.routerAccessIdIsUndefined(routerState);
    return result;
  }

  public async reloadOrder() {
    return await this.getOrderByAccessID();
  }

  protected async ensureOrder() {
    const currentAccessId = this.accessIdService.currentAccessId();
    const initializedAndNoAccessId = currentAccessId === undefined && this.accessIdService.accessIdState$.getValue().initialized;
    const orderIsNotCreating = this.orderState$.getValue().order === undefined && !this.creatingOrder();
    if (initializedAndNoAccessId && this.partnerStateService.partner && orderIsNotCreating) {
      this.creatingOrder.set(true);
      try {
        await this.createOrder([]);
      } finally {
        this.creatingOrder.set(false);
      }
    } else if (orderIsNotCreating && currentAccessId) {
      this.getOrderByAccessID()
    }
  }


  protected handleAccessIdUpdate(accessId?: string) {
    const currentAccessId = this.accessIdService.currentAccessId()
    const accessIdSet = currentAccessId === undefined && accessId;
    const isDifferentAccessId = currentAccessId !== accessId;
    if (isDifferentAccessId || accessIdSet) {
      // TODO this.accessId = accessId;
      this.getOrderByAccessID();
    }
  }

  private resetPollingStateOnFailure() {
    this.pollingState$.next(false);
    const state = this.orderState$.getValue();
    this.orderState$.next({
      ...state,
      error: {
        message: 'order could not be updated',
        name: 'order state invalid',
      },
    });
  }

  public async pollOrder(
    expected: (order: OrderOutput) => boolean,
    options?: { polls?: number; exec?: () => void; delay?: number },
  ) {
    this.pollingState$.next(true);
    const polls = options?.polls ?? 15;
    const delay = options?.delay ?? 1000;
    const exec = options?.exec;
    if (polls <= 0) {
      return this.resetPollingStateOnFailure();
    }
    try {
      const order = await this.getOrderByAccessIDWithoutUpdatingState();
      if (order && expected(order)) {
        this.pollingState$.next(false);
        this.orderState$.next({
          order: order,
          isLoading: false,
          error: undefined,
        });
        exec?.();
      } else {
        setTimeout(
          () => this.pollOrder(expected, { ...options, polls: polls - 1 }),
          delay,
        );
      }
    } catch {
      this.resetPollingStateOnFailure();
    }
  }

  public async resetOrderAndCreateNew() {
    this.router.clearState();
    this.orderState.next({
      order: undefined,
      isLoading: true,
      error: undefined,
    });
      await this.router.clearState();
      window.location.reload();
  }

  public async createOrUpdateOrder(order: {
    items?: OrderItemCreate[];
    userData?: UserData;
    propertyData?: PropertyData;
  }) {
    const currentOrder = this.orderState$.getValue().order;

    if (currentOrder) {
      const updatedOrder = {
        ...currentOrder,
        userData: this.patchUserData(currentOrder.userData, order.userData),
        propertyData: this.patchPropertyData(
          currentOrder.propertyData,
          order.propertyData,
        ),
        items:
          order.items
            ?.map((selected) => {
              const product = this.productService.productListState
                .getValue()
                .productList.find(
                  (item) => item.productType === selected.productType,
                );
              if (product) {
                const orderItem: OrderItem = {
                  itemId: '',
                  itemStatus: ItemStatus.NONE,
                  productData: {
                    ...product,
                    productType: selected.productType,
                  },
                };
                return orderItem;
              } else {
                return;
              }
            })
            .filter((v) => v !== undefined) ?? currentOrder.items,
      };

    this.handleUpdatedOrderOutput(updatedOrder);
    this.thresholdService.withThreshold(async () => {
      this.syncLocalToRemote();
    }, 1000);

    } else {
      this.createOrder(order.items ?? []);
    }
  }

  public getSelectedProductsCount(): number {
    const currentOrder = this.orderState$.value.order;
    if (!currentOrder) return 0;
    return currentOrder.items.length;
  }

  public async sendPOAMail(email: string) {
    const orderId = this.orderState$.getValue().order?.orderId;
    if (!orderId) throw new Error('No order id found');

    const command = new SendPOAEMailCommand({
      orderId,
      body: {
        email,
      },
    });
    this.apiService.addMiddleware(command);
    const response = await this.apiService.client?.send(command);
    if(response?.$metadata.httpStatusCode === 204) {
      this.toastState.set(true);
      setTimeout(() => {
        this.toastState.set(false);
      }, 7000);
    }
  }

  public async sendCancellationMail(input: {
    firstName: string;
    lastName: string;
    email: string;
    orderNumber: string;
    documentName?: string;
  }) {
    const command = new SendCancellationEMailCommand({
      body: input,
    });
    return this.apiService.client?.send(command);
  }

  public async fetchCustomerInfo() {
    const order = this.orderState$.getValue().order;
    const propertyAddress = order?.propertyData?.propertyAddress;
    const userPersonalData = order?.userData?.userPersonalData;
    return {
      emailAddress: order?.userData?.email ?? '',
      reciever: `${userPersonalData?.firstName ?? ''} ${userPersonalData?.lastName ?? ''}`,
      realEstateAddress: `${propertyAddress?.street ?? ''} ${propertyAddress?.streetNumber ?? ''}, ${propertyAddress?.zipcode ?? ''} ${propertyAddress?.city ?? ''}`,
    };
  }
}
